import * as React from 'react';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import Dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import debounce from 'lodash/debounce';
import { MockupGenerationLogs } from '../../store/slices';
import { IMockupGenerationLogsQueryParams } from '../../api/mockupGenerationLogs';
import { FilterBar, useFilterBarStyles } from '../ui';
import { dateParam } from '../../lib/orderFilters';

const STATUS_OPTIONS = [
    { value: '', label: 'All' },
    { value: 'ok', label: 'OK' },
    { value: 'error', label: 'Error' },
];

const HTTP_STATUS_OPTIONS = [
    { value: '', label: 'All' },
    { value: '200', label: '200 OK' },
    { value: '400', label: '400 Validation' },
    { value: '422', label: '422 Silhouette' },
    { value: '500', label: '500 Background removal' },
    { value: '503', label: '503 Unavailable' },
];

const RESPONSE_MODE_OPTIONS = [
    { value: '', label: 'All' },
    { value: 'glb', label: 'glb' },
    { value: 'json', label: 'json' },
];

const SORT_DIRECTIONS = [
    { value: 'DESC', label: 'Newest first' },
    { value: 'ASC', label: 'Oldest first' },
];

interface Props {
    queryParams: IMockupGenerationLogsQueryParams;
    loadLogs: (params: IMockupGenerationLogsQueryParams) => void;
    loading: boolean;
}

export default function MockupGenerationLogsFilters({ queryParams, loadLogs, loading }: Props) {
    const dispatch = useDispatch();
    const classes = useFilterBarStyles();
    const [clear, setClear] = useState(false);

    useEffect(() => {
        setClear(false);
    }, [loading]);

    const handleFilterChange = (name: keyof IMockupGenerationLogsQueryParams, value: any) => {
        loadLogs({ ...queryParams, page: 1, [name]: value });
    };

    // The endpoint requires ts_min and ts_max together; clearing one bound
    // falls back to a sensible value instead of silently dropping the range.
    const handleDateChange = (name: 'tsMin' | 'tsMax', value: string | null) => {
        const next: IMockupGenerationLogsQueryParams = { ...queryParams, page: 1, [name]: value };
        if (!next.tsMax) next.tsMax = Dayjs().format('YYYY-MM-DD');
        if (!next.tsMin) next.tsMin = Dayjs(next.tsMax).subtract(6, 'day').format('YYYY-MM-DD');
        if (next.tsMin > next.tsMax) {
            if (name === 'tsMin') next.tsMax = next.tsMin;
            else next.tsMin = next.tsMax;
        }
        loadLogs(next);
    };

    const handleClear = () => {
        dispatch(MockupGenerationLogs.clearMockupGenerationLogsFilters());
        setClear(true);
        loadLogs(MockupGenerationLogs.DEFAULT_MOCKUP_GENERATION_LOGS_PARAMS());
    };

    // Remounted after "Clear" (the `clear` flag) so the uncontrolled fields empty.
    const textFilter = (name: keyof IMockupGenerationLogsQueryParams, label: string) => (
        !clear ? (
            <TextField
                key={name}
                className={classes.text}
                label={label}
                size="small"
                defaultValue={queryParams?.[name] ?? ''}
                disabled={loading}
                onChange={debounce((e) => handleFilterChange(name, e.target.value || null), 500)}
            />
        ) : null
    );

    const onDate = (name: 'tsMin' | 'tsMax') => (value: Dayjs.Dayjs | null) => {
        if (value === null) {
            handleDateChange(name, null);
            return;
        }
        const iso = dateParam(value);
        if (iso) handleDateChange(name, iso);
    };

    const select = (name: keyof IMockupGenerationLogsQueryParams, label: string, value: string, options: { value: string; label: string }[], parse: (v: string) => any) => (
        <FormControl className={classes.select} size="small" key={name}>
            <InputLabel id={`gen-logs-${name}-label`}>{label}</InputLabel>
            <Select
                labelId={`gen-logs-${name}-label`}
                value={value}
                label={label}
                disabled={loading}
                onChange={(e) => handleFilterChange(name, parse(String(e.target.value)))}
            >
                {options.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
        </FormControl>
    );

    return (
        <FilterBar label="Generation log filters">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                    className={classes.date}
                    label="From (UTC)"
                    format="YYYY-MM-DD"
                    value={queryParams?.tsMin ? Dayjs(queryParams.tsMin) : null}
                    maxDate={queryParams?.tsMax ? Dayjs(queryParams.tsMax) : Dayjs(new Date())}
                    disabled={loading}
                    onChange={onDate('tsMin')}
                    slotProps={{ textField: { size: 'small' } }}
                />
                <DatePicker
                    className={classes.date}
                    label="To (UTC)"
                    format="YYYY-MM-DD"
                    value={queryParams?.tsMax ? Dayjs(queryParams.tsMax) : null}
                    minDate={queryParams?.tsMin ? Dayjs(queryParams.tsMin) : undefined}
                    maxDate={Dayjs(new Date())}
                    disabled={loading}
                    onChange={onDate('tsMax')}
                    slotProps={{ textField: { size: 'small' } }}
                />
            </LocalizationProvider>
            {select('status', 'Status', queryParams?.status ?? '', STATUS_OPTIONS, (v) => v || null)}
            {select('httpStatus', 'HTTP status', queryParams?.httpStatus != null ? String(queryParams.httpStatus) : '', HTTP_STATUS_OPTIONS, (v) => (v === '' ? null : Number(v)))}
            {select('responseMode', 'Response', queryParams?.responseMode ?? '', RESPONSE_MODE_OPTIONS, (v) => v || null)}
            {textFilter('modelType', 'Model type')}
            {textFilter('eventId', 'Event ID')}
            {textFilter('tempEventId', 'Temp event ID')}
            {textFilter('customerId', 'Customer ID')}
            {textFilter('template', 'Template')}
            {textFilter('serverHost', 'Server host')}
            {select('sortDirection', 'Sort', queryParams?.sortDirection || 'DESC', SORT_DIRECTIONS, (v) => v)}
            <Button className={classes.clear} variant="text" size="small" onClick={handleClear} disabled={loading}>
                Clear filters
            </Button>
        </FilterBar>
    );
}
